<template>
    <div class="sfServiceBookingForm" ref="form">
        <div class="sfServiceBookingForm__tabs">
            <button type="button"
                    class="sfServiceBookingForm__tab"
                    :class="{ 'sfServiceBookingForm__tab--active': type.value === vehicleType }"
                    v-for="type in vehicleTypes"
                    :key="type.value"
                    @click="onVehicleType(type.value)"
            >
                <Component :is="type.logo" :is-white="type.value === vehicleType" v-if="type.logo" />
                <span :class="{ 'sr-only': type.logo }">{{ type.label }}</span>
            </button>
        </div>

        <div class="sfServiceBookingForm__body">
            <form class="sfServiceBookingForm__form" @submit.prevent="onSubmit">

                <div class="sfServiceBookingForm__row">
                    <label for="vrm" class="sr-only">Registration number</label>
                    <div class="sfServiceBookingForm__inputWrap">
                        <input type="text"
                               name="reg_num"
                               id="vrm"
                               v-model="vrm"
                               placeholder="Enter vehicle reg"
                               class="sfServiceBookingForm__input"
                               @input="onInput"
                        />
                    </div>
                </div>

                <div class="sfServiceBookingForm__row">
                    <label for="mileage" class="sr-only">Mileage</label>
                    <div class="sfServiceBookingForm__inputWrap">
                        <input type="number"
                               name="mileage"
                               id="mileage"
                               v-model.number="mileage"
                               placeholder="Enter mileage"
                               class="sfServiceBookingForm__input"
                               @input="onInput"
                        />
                    </div>
                </div>

                <div class="sfServiceBookingForm__row">
                    <label for="location" class="sr-only">Location</label>
                    <div class="sfServiceBookingForm__selectWrap">
                        <select name="location"
                                id="location"
                                class="sfServiceBookingForm__select"
                                v-model="location"
                                @change="onInput"
                        >
                            <option value="">Location</option>
                            <option :value="location.id" v-for="location in locationsByVehicleType">
                                {{ location.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <button type="submit" class="sfServiceBookingForm__button" :disabled="isFormDisabled">
                    Book now
                </button>
            </form>

            <div class="sfServiceBookingForm__iframeContainer"
                 :class="{ 'sfServiceBookingForm__iframeContainer--flyout': isFlyout && valid && isFlyoutOpen }"
                 v-if="(isFlyout && isFlyoutOpen && valid) || (!isFlyout && valid)"
            >
                <button type="button"
                        class="sfServiceBookingForm__close"
                        v-if="isFlyout && isFlyoutOpen"
                        @click="onFlyoutClose"
                >
                    <span class="sfServiceBookingForm__close__lines"></span>
                    <span class="sr-only">Close</span>

                </button>
                <iframe :src="iframeUrl" width="100%" height="600"></iframe>
            </div>
        </div>
    </div>
</template>

<script>
import BMWLogo from './icons/brands/BMWLogo.vue'
import LandRoverLogo from './icons/brands/LandRoverLogo.vue'
import MiniLogo from './icons/brands/MiniLogo.vue'
import JaguarLogo from './icons/brands/JaguarLogo.vue'

export default {
    components: {
        BMWLogo,
        LandRoverLogo,
        MiniLogo,
        JaguarLogo
    },
    props: {
        utmMedium: {
            type: String,
            default: ''
        },
        utmCampaign: {
            type: String,
            default: ''
        },
        utmSource: {
            type: String,
            default: ''
        },
        locations: {
            type: Array,
            default: () => []
        },
        isTestMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            vehicleType: 'bmw',
            vrm: '',
            mileage: '',
            location: '',
            formErrors: {},
            valid: false,
            windowWidth: 0,
            isFlyoutOpen: false,
        }
    },
    created() {
        this.windowWidth = window.innerWidth;
        window.addEventListener('resize', this.setWindowWidth);
    },
    destroyed() {
        window.removeEventListener('resize', this.setWindowWidth)
    },
    methods: {
        setWindowWidth() {
            this.windowWidth = window.innerWidth;
        },
        onSubmit() {
            this.valid = true

            if (this.isFlyout) {
                this.isFlyoutOpen = true
            }
        },
        onVehicleType(value) {
            this.location = ''
            this.vehicleType = value
            this.valid = false
        },
        onInput() {
            this.valid = false
        },
        onFlyoutClose() {
            this.isFlyoutOpen = false
            this.valid = false
        }
    },
    watch: {
        windowWidth(val) {
            if (val > 670 && this.isFlyoutOpen) {
                this.isFlyoutOpen = false
            }
        }
    },
    computed: {
        isFormDisabled() {
            return this.vrm === '' || this.mileage === '' || this.location === ''
        },
        iframeUrl() {
            // Please note; reg_num param needs to come first as anywhere else encodes with &reg to (r) and SF doesn't parse these correctly if sent as url encoded & (%26)
            // TEST URL: 'https://keyloopmotors.devservicestorefront.co.uk/processing-request?&reg_num=OY62VSD&location=J2060J0727T&mileage=17000'

            // Show only test link until ready to go live as Keyloop have flagged testing real URL will skew sessions
            if (this.isTestMode) {
                return `https://keyloopmotors.devservicestorefront.co.uk/processing-request?reg_num=${this.vrm}&location=J2060J0727T&mileage=${this.mileage}&utm_source=${this.utmSource}&utm_medium=${this.utmMedium}&utm_campaign=${this.utmCampaign}`
            }

            //return `https://keyloopmotors.devservicestorefront.co.uk/processing-request?reg_num=${this.vrm}&location=J2060J0727T&mileage=${this.mileage}&utm_source=${this.utmSource}&utm_medium=${this.utmMedium}&utm_campaign=${this.utmCampaign}`

            return `https://petervardy.servicestorefront.co.uk/processing-request?reg_num=${this.vrm}&location=${this.location}&mileage=${this.mileage}&utm_source=${this.utmSource}&utm_medium=${this.utmMedium}&utm_campaign=${this.utmCampaign}`
        },
        vehicleTypes() {
            return [
                // {
                //     label: 'All Vehicles',
                //     value: 'all',
                //     logo: null
                // },
                {
                    label: 'BMW',
                    value: 'bmw',
                    logo: 'BMWLogo'
                },
                {
                    label: 'Jaguar',
                    value: 'jaguar',
                    logo: 'JaguarLogo'
                },
                {
                    label: 'Land Rover',
                    value: 'land-rover',
                    logo: 'LandRoverLogo'
                },
                {
                    label: 'MINI',
                    value: 'mini',
                    logo: 'MiniLogo'
                }
            ]
        },
        selectedVehicleType() {
            return this.vehicleTypes.find((v) => v.value === this.vehicleType)
        },
        locationsByVehicleType() {
            // if a franchise, it should get the main franchise as well as CARZ locations
            const carzLocations = this.locations.filter((l) => l.name.toLowerCase().includes('carz')).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            const franchiseLocations = this.locations.filter((l) => !l.name.toLowerCase().includes('carz')).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

            if (this.vehicleType === 'all') {
                return [...franchiseLocations, ...carzLocations]
            }

            const selectedVehicleTypeLocations = franchiseLocations.filter((l) => l.name.toLowerCase().includes(this.selectedVehicleType.label.toLowerCase()))

            return [...selectedVehicleTypeLocations, ...carzLocations ]
        },
        isFlyout() {
            return this.windowWidth <= 670
        }
    },
}
</script>